/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useState, ChangeEvent } from 'react';
import { Alert, Box, Button, Grid, LinearProgress, Paper, Snackbar, SnackbarCloseReason, alpha, styled } from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';
import { AutoCompleteMunicipio, VSwitch, VTextFieldCep, VTextFieldCpfCnpj, cpfcnpjValidator } from '../../../shared/forms';
import { VForm, useVForm, IVFormErrors, VTextField, SelectNumber } from '../../../shared/forms';
import { FerramentasDeDetalhe, TelaBloqueioSalvando } from '../../../shared/components';
import { useBloqueioSalvamento } from '../../../shared/hooks';
import { LayoutBaseDePagina } from '../../../shared/layouts';
import { ParceirosService } from '../../../shared/services/api/cadastros/ParceirosService';
import { CpfService } from '../../../shared/services/api/cadastros/cpf';
import { CnpjService } from '../../../shared/services/api/cadastros/CnpjService';
import { AutoCompleteCfop } from '../../../shared/forms/AutoCompleteCfop';
import { Environment } from '../../../shared/environment';
const StyledPaper = styled(Paper)(({ theme }) => ({
  borderRadius: '16px',
  background: theme.palette.mode === 'dark'
    ? `linear-gradient(145deg, ${alpha(theme.palette.background.paper, 0.9)}, ${alpha(theme.palette.background.paper, 0.7)})`
    : `linear-gradient(145deg, ${alpha(theme.palette.background.paper, 0.9)}, ${alpha(theme.palette.background.paper, 0.7)})`,
  backdropFilter: 'blur(10px)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  padding: theme.spacing(3),
  overflow: 'hidden',
}));

const ModernLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 6,
  borderRadius: 3,
  margin: theme.spacing(1, 0),
}));

interface IFormData {
  nome: string,
  cpf: string,
  fantasia: string,
  rg: string,
  telefone: string,
  celular: string,
  endereco: string,
  numero: string,
  bairro: string,
  idmunicipio: string,
  cep: string,
  email: string,
  obs: string,
  cfop: number,
  contribuinte: number,
  presenca: number,
  consumidorfinal: number,
  emailnfe: string;
  enviaremail: boolean;
}

const formValidationSchema: yup.ObjectSchema<Omit<IFormData, 'idoperacao' | 'totalparceiros'>> = yup.object().shape({
  nome: yup.string().required().min(3).max(100),
  cpf: yup.string().required().min(11).max(14).test('cpf', 'CPF / CNPJ inválido', cpfcnpjValidator),
  fantasia: yup.string().optional().default(''),
  rg: yup.string().default('').when('contribuinte', { is: 1, then: (schema) => schema.required('RG é obrigatório para contribuinte'), }),
  telefone: yup.string().optional().default('').max(16),
  celular: yup.string().optional().default('').max(16),
  endereco: yup.string().required().min(3).max(150),
  numero: yup.string().required().min(1).max(15),
  bairro: yup.string().required().min(3).max(150),
  idmunicipio: yup.string().required(),
  cep: yup.string().required().min(8).max(8),
  email: yup.string().email().default(''),
  obs: yup.string().optional().default(''),
  cfop: yup.number().required(),
  contribuinte: yup.number().required().required(),
  presenca: yup.number().required().required(),
  consumidorfinal: yup.number().required().required(),
  emailnfe: yup.string().email().default(''),
  enviaremail: yup.boolean().required().default(false),
});
export const DetalheDeParceiros: React.FC = () => {
  const { id = 'novo' } = useParams<'id'>();
  const navigate = useNavigate();
  const location = useLocation();
  const voltarParaListagem = (location.state as { from?: string } | null)?.from || '/parceiros';
  const { formRef, save, saveAndClose } = useVForm();
  const [isLoading, setIsLoading] = useState(false);
  const { isSaving, progress: saveProgress, iniciar: iniciarSalvamento, concluir: concluirSalvamento, cancelar: cancelarSalvamento } = useBloqueioSalvamento();
  const [descricao, setDescricao] = useState<string>('');
  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [tms, setTms] = useState<'warning' | 'error' | 'info' | 'success'>('warning');
  const [openConfirm, setOpenConfirm] = useState(false);
  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenmsg(false);
    if (tms === 'success') {
      navigate(voltarParaListagem);
    }
  };

  const handleDelete = () => {
    setOpenConfirm(true);
  };

  const handleConfirmDelete = () => {
    if (id !== Environment.URL_IDPARCEIRO) {
      ParceirosService.deleteById(id)
        .then(result => {
          if (result instanceof Error) {
            setOpenConfirm(false);
            setTms('error');
            setMsg(result.message);
            setOpenmsg(true);
          } else {
            setOpenConfirm(false);
            setTms('success');
            setMsg('Registro apagado com sucesso!');
            setOpenmsg(true);
          }
        });
    } else {
      setOpenConfirm(false);
      setTms('error');
      setMsg('Esse Registro não pode ser apagado!');
      setOpenmsg(true);
    }

  };

  const handleCancelDelete = () => {
    setOpenConfirm(false);
  };

  function sanitizeFormData<T extends object>(data: T): Partial<T> {
    const sanitized: Partial<T> = {};

    (Object.entries(data) as [keyof T, T[keyof T]][]).forEach(
      ([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          sanitized[key] = value;
        }
      }
    );

    return sanitized;
  }

  useEffect(() => {
    if (id !== 'novo') {
      setIsLoading(true);
      ParceirosService.getById(id)
        .then((result) => {
          setIsLoading(false);
          if (result instanceof Error) {
            setTms('error');
            setMsg(result.message);
            setOpenmsg(true);
            navigate(voltarParaListagem);
          } else {
            setDescricao(result.nome);
            const sanitized = sanitizeFormData(result);

            const cfopNumber = Number(sanitized.cfop);

            formRef.current?.setData({
              ...sanitized,
              cfop:
                cfopNumber === 0
                  ? null
                  : isNaN(cfopNumber)
                    ? undefined
                    : cfopNumber,
            });
          }
        });
    } else {
      formRef.current?.setData({
        nome: '',
        cpf: '',
        fantasia: '',
        rg: '',
        telefone: '',
        celular: '',
        endereco: '',
        numero: '',
        bairro: '',
        idmunicipio: '',
        cep: '',
        email: '',
        obs: '',
        cfop: '',
        contribuinte: 9,
        presenca: 1,
        consumidorfinal: 1,
        emailnfe: '',
        enviaremail: false,
      });
    }
  }, [id]);

  const handleSave = (dados: IFormData) => {
    if (!iniciarSalvamento()) return;

    formValidationSchema
      .validate(dados, { abortEarly: false })
      .then((dadosValidados) => {
        setIsLoading(true);
        if (id === 'novo') {
          ParceirosService
            .create(dadosValidados)
            .then((result) => {
              setIsLoading(false);
              if (result instanceof Error) {
                cancelarSalvamento();
                setTms('error');
                setMsg(result.message);
                setOpenmsg(true);
              } else {
                setTms('success');
                setMsg('Lançamento Realizado com sucesso!');
                setOpenmsg(true);
                concluirSalvamento();
              }
            });
        } else {
          if (id !== Environment.URL_IDPARCEIRO) {
            ParceirosService
              .updateById(id, dadosValidados)
              .then((result) => {
                setIsLoading(false);
                if (result instanceof Error) {
                  cancelarSalvamento();
                  setTms('error');
                  setMsg(result.message);
                  setOpenmsg(true);
                } else {
                  setTms('success');
                  setMsg('Alteração concluída com sucesso!');
                  setOpenmsg(true);
                  concluirSalvamento();
                }
              });
          } else {
            cancelarSalvamento();
            setOpenConfirm(false);
            setTms('error');
            setMsg('Esse Registro não pode ser alterado!');
            setOpenmsg(true);
          }
        }
      })
      .catch((errors: yup.ValidationError) => {
        cancelarSalvamento();
        const validationErrors: IVFormErrors = {};
        errors.inner.forEach(error => {
          if (!error.path) return;
          validationErrors[error.path] = error.message;
        });
        formRef.current?.setErrors(validationErrors);
      });
  };

  const handleCPFCNPJChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const fieldName = 'cpf';
    const enteredValue = event.target.value.replace(/[^\d]/g, '');
    try {
      await formValidationSchema.validateAt(fieldName, { [fieldName]: enteredValue });
      if ((enteredValue.length === 11 || enteredValue.length === 14) && enteredValue) {
        setIsLoading(true);
        try {
          const response = await CpfService.getAll(enteredValue);
          setIsLoading(false);
          if (typeof response !== 'string') {
            const isErrorResponse = 'message' in response && 'name' in response;
            if (isErrorResponse) {
              console.error('Erro ao consultar a API:', response);
              return;
            }
            throw new Error('Resposta inesperada da API');
          }
          const data: string = response;
          if (data !== 'false') {
            navigate(`/parceiros/${data}`, { state: { from: voltarParaListagem } });
          } else if (enteredValue.length === 14 && data === 'false') {
            setIsLoading(true);
            const cnpjData = await CnpjService.getAll(enteredValue);
            if (Array.isArray(cnpjData) && cnpjData.length > 0) {
              const dataAtIndex0 = cnpjData[0];
              const hasUndefinedFields = Object.values(dataAtIndex0).some(value => value === undefined);
              if (hasUndefinedFields) {
                setTms('error');
                setMsg('CNPJ não encontrado na SEFAZ');
                setOpenmsg(true);
              } else {
                formRef.current?.setData(
                  sanitizeFormData({
                    ...formRef.current?.getData(),
                    ...dataAtIndex0,
                  })
                );
              }
            } else {
              setTms('error');
              setMsg('CNPJ não encontrado na SEFAZ');
              setOpenmsg(true);
            }
          }
        } catch (error) {
          // console.error('Erro ao consultar a API:', error);
        } finally {
          setIsLoading(false);
        }
      }
    } catch (error) {
      setIsLoading(false);
    }
  };

  return (
    <>
      <TelaBloqueioSalvando
        open={isSaving}
        message="Salvando parceiro..."
        progress={saveProgress}
      />
      <LayoutBaseDePagina
      titulo={id === 'novo' ? 'Novo Parceiros' : descricao}
      barraDeFerramentas={
        <FerramentasDeDetalhe
          textoBotaoNovo='Novo'
          mostrarBotaoSalvarEFechar={false}
          mostrarBotaoSalvarCarregando={isLoading}
          mostrarBotaoSalvarEFecharCarregando={isLoading}
          mostrarBotaoNovo={id !== 'novo'}
          mostrarBotaoApagar={id !== 'novo' && id !== Environment.URL_IDPARCEIRO}
          mostrarBotaoImpressao={false}
          mostrarBotaoSalvar={id !== Environment.URL_IDPARCEIRO}
          mostrarBotaoApagarCarregando={isLoading}
          mostrarBotaoImpressaoCarregando={isLoading}
          disableBotaoSalvar={isLoading || isSaving}
          aoClicarEmSalvar={save}
          aoClicarEmSalvarEFechar={saveAndClose}
          aoClicarEmVoltar={() => navigate(voltarParaListagem)}
          mostrarBotaoVoltarCarregando={isLoading}
          aoClicarEmApagar={() => handleDelete()}
          aoClicarEmNovo={() => navigate('/parceiros/novo', { state: { from: voltarParaListagem } })}
          mostrarBotaoNovoCarregando={isLoading}
        />
      }
    >
      <VForm ref={formRef} onSubmit={handleSave} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
        <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={openmsg} autoHideDuration={900} onClose={handleCloseMsg}>
          <Alert
            onClose={handleCloseMsg}
            severity={tms}
            variant="filled"
            sx={{
              width: '100%',
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
            }}
          >
            {msg}
          </Alert>
        </Snackbar>
        <Snackbar open={openConfirm} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert
            severity="warning"
            variant="filled"
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
            }}
            action={
              <>
                <Button color="inherit" size="small" onClick={handleConfirmDelete}>
                  Sim
                </Button>
                <Button color="inherit" size="small" onClick={handleCancelDelete}>
                  Não
                </Button>
              </>
            }
          >
            Tem certeza que deseja excluir este registro?
          </Alert>
        </Snackbar>
        <Box margin={1} display="flex" flexDirection="column">
          <StyledPaper elevation={0}>
            <Grid container spacing={3}>
              {isLoading && (
                <Grid item xs={12}>
                  <ModernLinearProgress variant='indeterminate' />
                </Grid>
              )}

              <Grid item xs={6} sm={6} md={4} lg={3} xl={3}>
                <VTextFieldCpfCnpj
                  fullWidth
                  size="small"
                  onChange={handleCPFCNPJChange}
                  name='cpf'
                  disabled={isLoading}
                  label='CPF / CNPJ'
                />
              </Grid>
              <Grid item xs={6} sm={6} md={4} lg={3} xl={3}>
                <VTextField
                  fullWidth
                  size="small"
                  name='rg'
                  label='RG / Inscrição Estadual'
                  disabled={isLoading}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={6} xl={6}>
                <VTextField
                  fullWidth
                  size="small"
                  name='nome'
                  label='Nome / Razão Social'
                  disabled={isLoading}
                />
              </Grid>

              {/* Linha 2: Fantasia/Apelido, Telefone, Celular */}
              <Grid item xs={12} sm={6} md={4} lg={6} xl={3}>
                <VTextField
                  fullWidth
                  size="small"
                  name='fantasia'
                  label='Apelido / Fantasia'
                  disabled={isLoading}
                />
              </Grid>
              <Grid item xs={6} sm={6} md={4} lg={3} xl={1.5}>
                <VTextField
                  fullWidth
                  size="small"
                  name='telefone'
                  label='Telefone'
                  disabled={isLoading}
                />
              </Grid>
              <Grid item xs={6} sm={6} md={4} lg={3} xl={1.5}>
                <VTextField
                  fullWidth
                  size="small"
                  name='celular'
                  label='Celular'
                  disabled={isLoading}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={6} xl={3}>
                <VTextField
                  fullWidth
                  size="small"
                  name='email'
                  label='E-mail'
                  disabled={isLoading}
                />
              </Grid>

              {/* Linha 3: Município, CEP, Número */}
              <Grid item xs={12} sm={6} md={4} lg={3} xl={3}>
                <AutoCompleteMunicipio
                  isExternalLoading={isLoading}
                />
              </Grid>
              <Grid item xs={6} sm={6} md={4} lg={3} xl={1.5}>
                <VTextFieldCep
                  fullWidth
                  size="small"
                  name='cep'
                  label='CEP'
                  disabled={isLoading}
                />
              </Grid>
              <Grid item xs={6} sm={6} md={4} lg={3} xl={1.5}>
                <VTextField
                  fullWidth
                  size="small"
                  name='numero'
                  label='Número'
                  disabled={isLoading}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3} xl={3}>
                <VTextField
                  fullWidth
                  size="small"
                  name='bairro'
                  label='Bairro'
                  disabled={isLoading}
                />
              </Grid>

              {/* Linha 4: Endereço completo */}
              <Grid item xs={12} sm={6} md={4} lg={3} xl={3}>
                <VTextField
                  fullWidth
                  size="small"
                  name='endereco'
                  label='Endereço'
                  disabled={isLoading}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3} xl={3}>
                <AutoCompleteCfop

                  isExternalLoading={isLoading}
                />
              </Grid>

              {/* Linha 6: Email Fiscal */}


              {/* Linha 5: Dados Fiscais quando contribuinte for 1 inscricao estadual e obrigatoria*/}
              <Grid item xs={12} sm={6} md={4} lg={6} xl={3}>
                <SelectNumber
                  name='contribuinte'
                  label='Contribuinte'
                  data={[
                    { nameBD: 1, descricao: 'Contribuinte ICMS (informar a IE do destinatário)' },
                    { nameBD: 2, descricao: 'Contribuinte isento de inscrição no cadastro de Contribuintes do ICMS' },
                    { nameBD: 9, descricao: 'Não Contribuinte, que pode ou não possuir Inscrição Estadual no Cadastro de Contribuintes do ICMS' },
                  ]}
                  disabled={isLoading}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3} xl={1}>
                <SelectNumber
                  name='consumidorfinal'
                  label='Consumidor Final'
                  data={[
                    { nameBD: 0, descricao: 'Não' },
                    { nameBD: 1, descricao: 'Sim' },
                  ]}
                  disabled={isLoading}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
                <SelectNumber
                  name='presenca'
                  label='Presença'
                  data={[
                    { nameBD: 0, descricao: 'Não' },
                    { nameBD: 1, descricao: 'Presencial' },
                    { nameBD: 2, descricao: 'Internet' },
                    { nameBD: 3, descricao: 'Tele Atendimento' },
                    { nameBD: 4, descricao: 'Entega Domicílio' },
                    { nameBD: 5, descricao: 'Presencial Fora Estabelecimento' },
                    { nameBD: 6, descricao: 'Outros' },
                  ]}
                  disabled={isLoading}
                />
              </Grid><Grid item xs={12} sm={6} md={4} lg={6} xl={3}>
                <VTextField
                  fullWidth
                  size="small"
                  name='emailnfe'
                  label='E-mail Fiscal'
                  disabled={isLoading}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3} xl={3}>
                <VSwitch
                  name='enviaremail'
                  label='Env.NFE'
                  fdisabled={isLoading}
                  aliamento='top'
                />
              </Grid>
              {/* Linha 5: Dados Fiscais quando consumidor final for 1 inscricao estadual e obrigatoria*/}




              {/* Linha 7: Observações */}
              <Grid item xs={12}>
                <VTextField
                  fullWidth
                  name='obs'
                  label='Observações'
                  multiline
                  rows={4}
                  disabled={isLoading}
                />
              </Grid>
            </Grid>
          </StyledPaper>
        </Box>
      </VForm>
    </LayoutBaseDePagina>
    </>
  );
};